import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { uniqueId } from 'lodash-es';
import { forwardRef, useMemo } from 'react';
import { renderWithTooltip } from '../Tooltip';
import { LabelWithHint } from '../Typography/LabelWithHint';
import { renderRequiredLabel } from './Label';
import { StyledLabel, StyledHelpText, StyledLabelWithHint } from './styled';
import { TextAreaRoot, StyledTextAreaContainer, StyledTextArea } from './TextArea.styled';
import { tokens } from './tokens';
import { InputVariants, ResizeDirection } from './types';
import { TriggerKind, useInput } from './useInput';
export const TextArea = /*#__PURE__*/ forwardRef(({ label, required = false, helpText, maxLength, className, style, disabled, variant = InputVariants.primary, value, resize = ResizeDirection.vertical, onChange, placeholder, placeholderSuggestion, delay = 250, hint, tooltip, id, onBlur, onFocus, autoGrow = false, rows, maxRows = Number.MAX_SAFE_INTEGER, renderTextArea = (node)=>node, ...restProps }, ref)=>{
    const textAreaId = useMemo(()=>id || uniqueId('textArea'), [
        id
    ]);
    const { _value, valueLength, handleChange, handleBlur } = useInput({
        value,
        onChange,
        delay,
        onBlur
    });
    const _onFocus = (e)=>{
        if (placeholderSuggestion) {
            e.currentTarget.value = placeholderSuggestion;
            e.currentTarget.select();
            handleChange(e, TriggerKind.Auto);
        }
        onFocus?.(e);
    };
    const labelUI = label ? /*#__PURE__*/ _jsx(StyledLabel, {
        htmlFor: textAreaId,
        children: label
    }) : null;
    const labelUIWrapper = renderRequiredLabel({
        label: labelUI,
        required
    });
    const _rows = autoGrow ? Math.min(maxRows, (_value || '').toString().split('\n').length) : rows;
    let textAreadUI = renderTextArea(/*#__PURE__*/ _jsx(StyledTextAreaContainer, {
        variant: variant,
        disabled: disabled,
        "data-placeholder": placeholder,
        "data-value": _value,
        children: /*#__PURE__*/ _jsx(StyledTextArea, {
            ref: ref,
            id: textAreaId,
            disabled: disabled,
            value: _value,
            rows: _rows,
            resize: resize,
            onChange: (e)=>{
                if (autoGrow) {
                    const currentTarget = e.currentTarget;
                    // Note: We manually set the rows to avoid height changing after the rerender
                    const rows = Math.min(maxRows, e.currentTarget.value.split('\n').length);
                    currentTarget.rows = rows;
                }
                handleChange(e, TriggerKind.User);
            },
            onBlur: handleBlur,
            onFocus: _onFocus,
            placeholder: placeholderSuggestion || placeholder,
            "data-placeholder": placeholder,
            "data-value": _value,
            maxLength: maxLength,
            ...restProps
        })
    }));
    if (tooltip) {
        textAreadUI = /*#__PURE__*/ _jsx(_Fragment, {
            children: renderWithTooltip(textAreadUI, tooltip)
        });
    }
    if (!label && hint) {
        textAreadUI = /*#__PURE__*/ _jsx(LabelWithHint, {
            hint: hint,
            children: textAreadUI
        });
    }
    return /*#__PURE__*/ _jsxs(TextAreaRoot, {
        style: style,
        className: className,
        gap: tokens.Root.gap,
        alignY: "stretch",
        children: [
            hint && label ? /*#__PURE__*/ _jsx(StyledLabelWithHint, {
                level: "xs",
                hint: hint,
                children: labelUIWrapper
            }) : labelUIWrapper,
            textAreadUI,
            helpText && /*#__PURE__*/ _jsxs(StyledHelpText, {
                variant: variant,
                direction: "horizontal",
                align: "space-between",
                children: [
                    helpText,
                    maxLength && maxLength > 0 ? /*#__PURE__*/ _jsx("span", {
                        children: valueLength + '/' + maxLength
                    }) : null
                ]
            })
        ]
    });
});
